import React, { useState, useMemo, useEffect } from 'react';
import { Grid, Paper, Typography, CircularProgress, Button, Box, Chip } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import MigaPan from '../../../app/orussystem/component/MigaPan';
import useStyles from './RegistrosDeUsoStyle';
import { MdFilterList } from 'react-icons/md';
import { FaDownload } from 'react-icons/fa';
import { FiltrosRegistro } from './components/filters';
import { ImplementationCard, ParticipationCard } from './components/cards';
import { DonutChart } from './components/charts';
import { TablaUsuarios } from './components/table';
import { MONTH_NAMES, PARTICIPATION_THRESHOLDS } from './constants';
import { useEstaciones, useHistoricoEncuesta, useExportHistorico, useFiltrosDinamicos } from './hooks';
import { concatenateStationIds, getStationDisplayName } from './components/filters/utils/stationUtils';

/**
 * Componente principal de Registros de Uso
 * Muestra estadísticas de uso de estaciones EVBI con:
 * - Panel de filtros (estación, año, mes, empresa, área, cargo)
 * - Tarjetas de métricas (Implementación y Participación)
 * - Gráfica de dona (distribución de usuarios)
 * - Tabla de usuarios con paginación, ordenamiento y búsqueda
 * 
 * @component
 * @returns {JSX.Element} Vista completa de Registros de Uso
 */
export default function RegistrosDeUso() {
    const classes = useStyles();
    const theme = useTheme();
    const [, primaryColor] = theme.palette.primary.main;

    const { estaciones, loading: loadingEstaciones, error: errorEstaciones } = useEstaciones();
    const { metricas, usuarios, paginacion, loading: loadingHistorico, error: errorHistorico, fetchHistorico } = useHistoricoEncuesta();
    const { exportar, loading: loadingExport, error: errorExport } = useExportHistorico();
    const [totalUsersInput, setTotalUsersInput] = useState(500);
    const [showFilters, setShowFilters] = useState(true);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [closeStationSelector, setCloseStationSelector] = useState(false);
    const [closeCompanySelector, setCloseCompanySelector] = useState(false);
    const [closeAreaSelector, setCloseAreaSelector] = useState(false);
    const [closePositionSelector, setClosePositionSelector] = useState(false);
    const [filters, setFilters] = useState({
        station: [],
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        company: [],
        area: [],
        position: []
    });
    // Hook para cargar filtros dinámicos (Empresa, Área, Cargo)
    // Nota: Si se seleccionan múltiples estaciones, usa solo la primera para cargar los filtros
    // Esto permite mantener una UX consistente y evitar complejidad innecesaria
    const { companies, areas, positions, loading: loadingFiltros, error: errorFiltros, cargarFiltroEnCascada } = useFiltrosDinamicos(
        filters.station,
        estaciones
    );
    const [areasEnCascada, setAreasEnCascada] = useState([]);
    const [positionsEnCascada, setPositionsEnCascada] = useState([]);

    const getMonthName = (monthNumber) => {
        return MONTH_NAMES[monthNumber - 1] || '';
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearFilters = () => {
        setFilters({
            station: [],
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            company: [],
            area: [],
            position: []
        });
        setAreasEnCascada([]);
        setPositionsEnCascada([]);
        setAppliedFilters(null);
    };

    // Limpiar filtros de empresa/área/cargo cuando cambia la selección de estaciones
    useEffect(() => {
        // Solo limpiar si hay cambio real en la selección
        if (filters.station && (Array.isArray(filters.station) ? filters.station.length > 0 : filters.station)) {
            setFilters(prev => ({
                ...prev,
                company: [],
                area: [],
                position: []
            }));
            setAreasEnCascada([]);
            setPositionsEnCascada([]);
        }
    }, [filters.station]);

    // Cargar áreas en cascada cuando se selecciona una empresa
    useEffect(() => {
        if (filters.company && filters.company.length > 0 && companies.length > 0) {
            // Usar la primera empresa seleccionada para obtener el campo 'siguiente'
            const primerEmpresaId = filters.company[0];
            const empresaSeleccionada = companies.find(c => c.id === primerEmpresaId || c.value === primerEmpresaId);
            if (empresaSeleccionada && typeof empresaSeleccionada.siguiente === 'number') {
                const opcionesArea = cargarFiltroEnCascada(empresaSeleccionada.siguiente);
                setAreasEnCascada(opcionesArea);
                // Limpiar área y cargo cuando cambia la empresa
                setFilters(prev => ({
                    ...prev,
                    area: [],
                    position: []
                }));
                setPositionsEnCascada([]);
            }
        } else {
            setAreasEnCascada([]);
            setPositionsEnCascada([]);
        }
    }, [filters.company, companies, cargarFiltroEnCascada]);

    // Cargar cargos en cascada cuando se selecciona un área
    useEffect(() => {
        if (filters.area && filters.area.length > 0 && areasEnCascada.length > 0) {
            // Usar la primera área seleccionada para obtener el campo 'siguiente'
            const primerAreaId = filters.area[0];
            const areaSeleccionada = areasEnCascada.find(a => a.id === primerAreaId || a.value === primerAreaId);
            if (areaSeleccionada && typeof areaSeleccionada.siguiente === 'number') {
                const opcionesCargo = cargarFiltroEnCascada(areaSeleccionada.siguiente);
                setPositionsEnCascada(opcionesCargo);
                // Limpiar cargo cuando cambia el área
                setFilters(prev => ({
                    ...prev,
                    position: []
                }));
            }
        } else {
            setPositionsEnCascada([]);
        }
    }, [filters.area, areasEnCascada, cargarFiltroEnCascada]);

    const handleApplyFilter = () => {
        // Concatenar estaciones con "-" si hay múltiples
        const stationId = concatenateStationIds(filters.station);
        
        fetchHistorico({
            stationId: stationId,
            month: filters.month,
            year: filters.year,
            size: 600,
            filterEmpresa: concatenateStationIds(filters.company),
            filterArea: concatenateStationIds(filters.area),
            filterCargo: concatenateStationIds(filters.position)
        });
        setAppliedFilters({ ...filters });
        
        // Cerrar todos los selectores
        setCloseStationSelector(true);
        setCloseCompanySelector(true);
        setCloseAreaSelector(true);
        setClosePositionSelector(true);
        
        setTimeout(() => {
            setCloseStationSelector(false);
            setCloseCompanySelector(false);
            setCloseAreaSelector(false);
            setClosePositionSelector(false);
        }, 100);
    };

    const getStationName = (stationId) => {
        if (!stationId) return '';
        
        // Si es array, usar getStationDisplayName
        if (Array.isArray(stationId)) {
            return getStationDisplayName(stationId, estaciones);
        }
        
        // Si es string con múltiples IDs separados por "-"
        if (typeof stationId === 'string' && stationId.includes('-')) {
            const ids = stationId.split('-').map(id => {
                const parsed = parseInt(id.trim(), 10);
                return isNaN(parsed) ? id.trim() : parsed;
            });
            return getStationDisplayName(ids, estaciones);
        }
        
        // Si es un ID único
        const station = estaciones.find((e) => e.id === stationId || e.hash === stationId);
        return station ? station.name || station.nombre : stationId;
    };

    const getCompanyName = (companyIds) => {
        if (!companyIds) return '';
        const ids = Array.isArray(companyIds) ? companyIds : [companyIds];
        if (ids.length === 0) return '';
        if (ids.length === 1) {
            const company = companies.find((c) => c.id === ids[0] || c.value === ids[0]);
            return company ? company.label : ids[0];
        }
        return `${ids.length} seleccionadas`;
    };

    const getAreaName = (areaIds) => {
        if (!areaIds) return '';
        const ids = Array.isArray(areaIds) ? areaIds : [areaIds];
        if (ids.length === 0) return '';
        if (ids.length === 1) {
            const area = areasEnCascada.find((a) => a.id === ids[0] || a.value === ids[0]);
            return area ? area.label : ids[0];
        }
        return `${ids.length} seleccionadas`;
    };

    const getPositionName = (positionIds) => {
        if (!positionIds) return '';
        const ids = Array.isArray(positionIds) ? positionIds : [positionIds];
        if (ids.length === 0) return '';
        if (ids.length === 1) {
            const position = positionsEnCascada.find((p) => p.id === ids[0] || p.value === ids[0]);
            return position ? position.label : ids[0];
        }
        return `${ids.length} seleccionadas`;
    };

    const handleDownload = () => {
        exportar({
            stationName: getStationName(filters.station),
            stationId: filters.station,
            month: filters.month,
            year: filters.year,
            filterEmpresa: concatenateStationIds(filters.company),
            filterArea: concatenateStationIds(filters.area),
            filterCargo: concatenateStationIds(filters.position)
        });
    };

    // Transformar datos del API al formato de los componentes
    const implementationData = useMemo(() => {
        const measuredUsers = metricas?.usuarios_medidos || 0;
        const usersWithoutMeasurements = totalUsersInput - measuredUsers;
        const implementationPercentage = totalUsersInput > 0
            ? Math.round((measuredUsers / totalUsersInput) * 100)
            : 0;
        return {
            measuredUsers,
            totalUsers: totalUsersInput,
            implementationPercentage,
            usersWithoutMeasurements: Math.max(usersWithoutMeasurements, 0),
        };
    }, [metricas, totalUsersInput]);

    const participationData = useMemo(() => {
        const lowParticipationUsers = usuarios.filter(
            (u) => u.porcentaje_utilizacion < PARTICIPATION_THRESHOLDS.medium
        ).length;
        return {
            totalMeasurements: metricas?.total_mediciones || 0,
            averageParticipation: metricas?.promedio_utilizacion || 0,
            lowParticipationUsers,
        };
    }, [metricas, usuarios]);

    const chartData = useMemo(() => ({
        measuredUsers: metricas?.usuarios_medidos || 0,
        usersWithoutMeasurements: Math.max(totalUsersInput - (metricas?.usuarios_medidos || 0), 0),
    }), [metricas, totalUsersInput]);

    const tableData = useMemo(() => 
        usuarios.map((u, index) => ({
            id: index + 1,
            identificacion: u.documento || '',
            nombre: u.nombre || '',
            cargo: u.cargo || '',
            area: u.area || '',
            empresa: u.empresa_contratante || '',
            cantidadMediciones: u.mediciones || 0,
            participacion: u.porcentaje_utilizacion || 0,
        })),
        [usuarios]
    );

    return (
        <>
            <Grid item xs={12}>
                <br />
                <br />
                <MigaPan
                    namePage="Registros de Uso"
                    to="/"
                    namePage2="Datos"
                    to2="/Datos"
                >
                    Home
                </MigaPan>
            </Grid>

            <Paper className={classes.paper} style={{ height: '100%', width: '100%' }}>
                {/* Título */}
                <Typography className={classes.title} color="primary">
                    REGISTROS DE USO
                </Typography>

                {/* ============ SECCIÓN 1: FILTROS ============ */}
                <Box className={classes.filterSection}>
                    {/* Header de filtros con toggle y botones */}
                    <Box className={classes.filterHeader}>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                startIcon={<MdFilterList />}
                                onClick={() => setShowFilters(!showFilters)}
                                className={classes.filterToggleBtn}
                            >
                                Filtrar
                            </Button>
                            {appliedFilters && (
                                <>
                                    {appliedFilters.station && (Array.isArray(appliedFilters.station) ? appliedFilters.station.length > 0 : appliedFilters.station) && (
                                        <Chip
                                            label={`Estación${Array.isArray(appliedFilters.station) && appliedFilters.station.length > 1 ? 's' : ''}: ${getStationName(appliedFilters.station)}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    {appliedFilters.year && (
                                        <Chip
                                            label={`Año: ${appliedFilters.year}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    {appliedFilters.month && (
                                        <Chip
                                            label={`Mes: ${getMonthName(appliedFilters.month)}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    {appliedFilters.company && appliedFilters.company.length > 0 && (
                                        <Chip
                                            label={`Empresa${appliedFilters.company.length > 1 ? 's' : ''}: ${getCompanyName(appliedFilters.company)}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    {appliedFilters.area && appliedFilters.area.length > 0 && (
                                        <Chip
                                            label={`Área${appliedFilters.area.length > 1 ? 's' : ''}: ${getAreaName(appliedFilters.area)}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    {appliedFilters.position && appliedFilters.position.length > 0 && (
                                        <Chip
                                            label={`Cargo${appliedFilters.position.length > 1 ? 's' : ''}: ${getPositionName(appliedFilters.position)}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    <Typography
                                        variant="caption"
                                        style={{
                                            color: '#1976d2',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            fontSize: '12px',
                                            marginLeft: '4px'
                                        }}
                                        onClick={handleClearFilters}
                                    >
                                        Limpiar filtros
                                    </Typography>
                                </>
                            )}
                        </Box>
                        <Box className={classes.filterActions}>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                className={classes.filterBtn}
                                onClick={handleApplyFilter}
                            >
                                Filtro
                            </Button>
                            <Button 
                                variant="contained" 
                                startIcon={loadingExport ? <CircularProgress size={16} style={{ color: 'white' }} /> : <FaDownload />}
                                className={classes.downloadBtn}
                                onClick={handleDownload}
                                disabled={loadingExport || !filters.station}
                            >
                                {loadingExport ? 'DESCARGANDO...' : 'DESCARGAR'}
                            </Button>
                        </Box>
                    </Box>

                    {/* Collapsible filter panel */}
                    {showFilters && (
                        <Box className={classes.filterPanel}>
                            <FiltrosRegistro 
                                filters={filters}
                                onFilterChange={handleFilterChange}
                                stations={estaciones}
                                companies={companies}
                                areas={areas}
                                positions={positions}
                                areasEnCascada={areasEnCascada}
                                positionsEnCascada={positionsEnCascada}
                                loadingFiltros={loadingFiltros}
                                errorFiltros={errorFiltros}
                                onCloseStationSelector={closeStationSelector ? () => {} : null}
                                onCloseCompanySelector={closeCompanySelector ? () => {} : null}
                                onCloseAreaSelector={closeAreaSelector ? () => {} : null}
                                onClosePositionSelector={closePositionSelector ? () => {} : null}
                            />
                        </Box>
                    )}
                </Box>

                {loadingHistorico ? (
                    <div className={classes.loadingContainer}>
                        <CircularProgress />
                    </div>
                ) : (
                    <>
                        {/* ============ SECCIÓN 2: DATOS (Cards + Gráfica) ============ */}
                        <Box className={classes.dataSection}>
                            <Grid container spacing={4}>
                                {/* Implementation Card */}
                                <Grid item xs={12} md={4}>
                                    <ImplementationCard 
                                        data={implementationData}
                                        month={getMonthName(filters.month)}
                                        year={filters.year}
                                        onTotalUsersChange={(value) => setTotalUsersInput(value)}
                                    />
                                </Grid>

                                {/* Donut Chart */}
                                <Grid item xs={12} md={4}>
                                    <DonutChart 
                                        data={chartData}
                                        month={getMonthName(filters.month)}
                                        year={filters.year}
                                    />
                                </Grid>

                                {/* Participation Card */}
                                <Grid item xs={12} md={4}>
                                    <ParticipationCard 
                                        data={participationData}
                                        month={getMonthName(filters.month)}
                                        year={filters.year}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* ============ SECCIÓN 3: TABLA DE USUARIOS ============ */}
                        <Box className={classes.tableSection}>
                            <TablaUsuarios data={tableData} loading={loadingHistorico} />
                        </Box>
                    </>
                )}
            </Paper>
        </>
    );
}
